import { Signal, useComputed } from "@suse-o11y/signal";
import { nanoid } from "nanoid";
import React from "react";
import { useSelector } from "react-redux";
import useWebSocket from "react-use-websocket";

import {
	Type,
	type WebSocketStreamResponse,
	isInternalError,
	isInvalidStreamError,
	isStreamFailed,
} from "~/models/dto";
import { createURL } from "~/tools/create-url";
import { secondsToMilliseconds } from "~/tools/date-time";
import { fromJSON } from "~/tools/from-json";
import { ImmutableMap } from "~/tools/immutable-map";
import { isDeepEqual } from "~/tools/is-deep-equal";
import { Logger } from "~/tools/logger";
import type { StreamConfigFromManager, StreamMessageFromManager } from "~/tools/stream-manager";
import type { StreamStore } from "~/tools/stream-manager/store";
import { usePermanent } from "~/tools/use-permanent";

import { selectWebUiConfig } from "../../app/state/config/ConfigSelectors";
import { notification } from "../react/notification/Notification";

import { manager } from "./manager";
import { WebSocketContextProvider, type WebSocketContextValue } from "./websocket-context";

const logger = Logger.namespace("WebSocketProvider");

const WebSocketGroup: React.FC<{
	url: string;
	reconnectInterval: number;
	index: number;
	store: Signal<StreamStore<typeof manager>>;
	streams: ImmutableMap<string, StreamConfigFromManager<typeof manager>>;
}> = ({ url, reconnectInterval, index, store, streams }) => {
	const prevStreamsRef = React.useRef(
		ImmutableMap.empty<string, StreamConfigFromManager<typeof manager>>({ isValueEqual: isDeepEqual }),
	);

	const { sendJsonMessage } = useWebSocket(url, {
		queryParams: { group: index },
		onOpen: (ev) => logger.debug("WebSocket connection open: ", ev),
		onClose: (ev) => logger.debug("WebSocket connection closed: ", ev),
		onMessage: ({ data }: { data: string }) => {
			const [error, stream] = fromJSON<WebSocketStreamResponse>(data);

			if (error != null) {
				logger.error("Failed to parse WebSocket message: ", error);
				return;
			}

			store.write((current) => {
				const config = streams.get(stream.unsafe.streamType);

				if (config != null) {
					return manager.receive(
						config,
						stream.unsafe.response as StreamMessageFromManager<string, typeof manager>,
						current,
					);
				}

				return current;
			});
		},
		onError: (ev) => {
			logger.error("WebSocket error: ", ev);

			if (streams.size > 0 && (isInternalError(ev) || isInvalidStreamError(ev) || isStreamFailed(ev))) {
				notification.warning({ message: `Error on WebSocket stream: ${ev.message}` });
			}
		},
		onReconnectStop: (ev) => logger.debug("WebSocket reconnect stopped: ", ev),
		retryOnError: true,
		shouldReconnect: () => {
			logger.warn(`Server connectivity issues, retrying in ${reconnectInterval}ms`);
			return true;
		},
		reconnectAttempts: 5,
		reconnectInterval,
	});

	React.useEffect(() => {
		const removed = prevStreamsRef.current.subtract(streams);

		for (const streamType of removed.keys()) {
			sendJsonMessage({
				_type: Type.WebSocketStopStream,
				streamType,
			});
		}

		const added = streams.subtract(prevStreamsRef.current);

		for (const stream of added.values()) {
			sendJsonMessage({
				_type: Type.WebSocketStartStream,
				streamIdentifier: nanoid(),
				...stream,
			});
		}

		prevStreamsRef.current = streams;
	}, [streams, sendJsonMessage]);

	return null;
};

export const WebSocketProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const config = useSelector(selectWebUiConfig);
	const reconnectInterval = secondsToMilliseconds(config.streamReconnectTimeout);

	const url = createURL({
		protocol: "WS",
		secure: location.protocol === "https:",
		host: location.hostname,
		port: location.port,
		path: "/api/stream",
	});

	const context = usePermanent<WebSocketContextValue>(() => {
		const { store, registry } = manager.manage();

		return {
			store: Signal(store),
			registry: Signal(registry),
		};
	});

	const groups = useComputed((monitor) => context.registry.read(monitor).toGroups(), [context.registry], {
		equals: isDeepEqual,
	});

	return (
		<WebSocketContextProvider value={context}>
			{groups.map((group, index) => (
				<WebSocketGroup
					index={index}
					key={index}
					reconnectInterval={reconnectInterval}
					store={context.store}
					streams={group}
					url={url}
				/>
			))}
			{children}
		</WebSocketContextProvider>
	);
};
